'use client';

import { useBunja } from 'bunja/react';
import { differenceInHours } from 'date-fns';
import { useAtomValue } from 'jotai';

import { PendingReduction } from '@flipster/apis/v1/earn/types';
import { earnSummaryBunja, getEarnSummaryOfCurrency } from '@flipster/features/src/shared/earn/state';
import { Badge } from '@flipster/react/design-system/badge';
import { Datetime } from '@flipster/react/design-utils/datetime';
import Formatted from '@flipster/react/design-utils/formatted';
import ChevronDownIcon from '@flipster/react/icon/generated/ChevronDownIcon';
import InProgressIcon from '@flipster/react/icon/generated/InProgressIcon';
import { css } from '@flipster/styled-system/css';
import { hstack, square, stack } from '@flipster/styled-system/patterns';
import { UsdtCurrency, UsdtPrecision } from '@flipster/utils/constants/currency';
import { convertDate } from '@flipster/utils/datetime';

export const PendingReductions = ({ currency }: { currency: string }) => {
  const { earnSummaryAtom } = useBunja(earnSummaryBunja);
  const earnSummary = useAtomValue(earnSummaryAtom);
  const { pendingReductions } = getEarnSummaryOfCurrency(currency, earnSummary);

  const maxPendingReductionTime = pendingReductions.reduce((max, reduction) => {
    const scheduledAt = convertDate(reduction.scheduledAt);
    return scheduledAt && scheduledAt.getTime() > max.getTime() ? scheduledAt : max;
  }, new Date());

  if (pendingReductions.length === 1) {
    return (
      <div
        className={stack({
          borderTop: '1px solid',
          borderColor: 'border.default.level-1',
          py: 12,
        })}
      >
        <div className={hstack({ justify: 'space-between' })}>
          <span className={css({ textStyle: 'Text/M/Regular', color: 'content.default.level-4' })}>
            Reduction pending
          </span>
          <Badge type='outline' color='accent' leadingIcon={<InProgressIcon size={12} />}>
            {differenceInHours(maxPendingReductionTime ?? new Date(), new Date(), { roundingMethod: 'ceil' })} hrs to
            process
          </Badge>
        </div>
        <div className={css({ textStyle: 'Text/S/Regular', mt: 12 })}>
          <Formatted.Number
            value={pendingReductions[0].amount}
            precision={UsdtPrecision}
            toFixed
            css={{ color: 'content.default.level-4' }}
          />{' '}
          <span>{UsdtCurrency}</span>
          <p className={css({ color: 'content.default.level-2', mt: 2 })}>
            <Datetime value={pendingReductions[0].scheduledAt} format='MM/dd/yy HH:mm:ss' isUTC withUTCText />
          </p>
        </div>
      </div>
    );
  }

  return (
    <details
      className={stack({
        borderTop: '1px solid',
        borderColor: 'border.default.level-1',
        py: 12,
        cursor: 'pointer',
        userSelect: 'none',
      })}
    >
      <summary>
        <div className={hstack({ justify: 'space-between' })}>
          <div className={stack()}>
            <span className={css({ textStyle: 'Text/M/Regular', color: 'content.default.level-4' })}>
              Reduction pending
            </span>
            <span className={css({ textStyle: 'Text/S/Regular', color: 'content.default.level-2' })}>
              {pendingReductions.length} pending
            </span>
          </div>
          <div className={hstack({ gap: 4 })}>
            <div className={css({ flex: 1, textAlign: 'right' })}>
              <Badge type='outline' color='accent' leadingIcon={<InProgressIcon size={12} />}>
                {differenceInHours(maxPendingReductionTime ?? new Date(), new Date(), { roundingMethod: 'ceil' })} hrs
                to process
              </Badge>
            </div>
            <span
              className={square({
                size: 24,
                color: 'content.default.level-3',
                '[open] &': { transform: 'rotate(-180deg)' },
              })}
            >
              <ChevronDownIcon size={16} />
            </span>
          </div>
        </div>
      </summary>
      <div>
        {pendingReductions.map((reduction: PendingReduction) => (
          <div
            className={css({ textStyle: 'Text/S/Regular', color: 'content.default.level-4', mt: 12 })}
            key={reduction.scheduledAt}
          >
            <Formatted.Number
              value={reduction.amount}
              suffix={UsdtCurrency}
              precision={UsdtPrecision}
              toFixed
              css={{ display: 'inline' }}
            />
            <p className={css({ color: 'content.default.level-2', mt: 2 })}>
              <Datetime value={reduction.scheduledAt} format='MM/dd/yy HH:mm:ss' isUTC withUTCText />
            </p>
          </div>
        ))}
      </div>
    </details>
  );
};
